import { useEffect, useState } from "react";
import { Wallet, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { getCsrfToken } from "../lib/auth";
import { TeachingBanner } from "../components/TeachingBanner";

/** Simplifi connector settings. Mounted at
 *  /settings/connectors/simplifi. Quicken Simplifi has no public API
 *  and is web-only, so Memso authenticates with the session you
 *  already hold after logging in: open Simplifi in the paired Chrome
 *  tab, sign in, and the capture posts the session cookie here
 *  (stored per-user, encrypted when a master secret is set). When
 *  the session expires the connector parks itself needs_reauth and
 *  re-capturing here is the one-click recovery. */
interface SimplifiStatus {
  ok: boolean;
  paired: boolean;
  capturedAt: string | null;
  connector: {
    enabled: boolean;
    last_status: string | null;
    last_sync_at: string | null;
    needs_reauth: boolean;
  } | null;
}

export function SimplifiConfigure() {
  const [status, setStatus] = useState<SimplifiStatus | null>(null);
  const [cookie, setCookie] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      const r = await fetch("/api/connectors/simplifi/status", {
        credentials: "same-origin",
      });
      setStatus(await r.json());
    } catch {
      /* offline */
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function capture() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await fetch("/api/connectors/simplifi/session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-csrf-token": getCsrfToken() ?? "",
        },
        credentials: "same-origin",
        body: JSON.stringify({ cookie: cookie.trim() }),
      });
      const j = await r.json();
      if (!r.ok || j.error) {
        setErr(j.error || "capture failed");
      } else {
        setMsg("Session captured. Simplifi will sync on the next tick.");
        setCookie("");
        await load();
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-5">
      <div className="flex items-center gap-2">
        <Wallet className="w-5 h-5 text-emerald-600" />
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Quicken Simplifi
        </h1>
      </div>

      <TeachingBanner id="simplifi_overview" />

      {status?.paired ? (
        <div className="rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-3 text-sm text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" />
          Paired{status.capturedAt ? ` (captured ${status.capturedAt.slice(0, 10)})` : ""}.
          {status.connector?.needs_reauth
            ? " Session expired - re-capture below to resume."
            : status.connector?.last_status
              ? ` Last: ${status.connector.last_status}`
              : ""}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 text-sm text-slate-600 dark:text-slate-300">
          Not paired yet.
        </div>
      )}

      <ol className="text-sm text-slate-700 dark:text-slate-300 space-y-2 list-decimal pl-5">
        <li>Open Simplifi in the paired Chrome tab and sign in.</li>
        <li>
          Memso captures the session cookie from that tab. If you are
          pasting manually: in the Simplifi tab open DevTools console and
          run <code>copy(document.cookie)</code>, then paste below.
        </li>
        <li>Click Capture. Sync runs every 15 minutes when enabled.</li>
      </ol>

      <textarea
        value={cookie}
        onChange={(e) => setCookie(e.target.value)}
        placeholder="Paste the Simplifi session cookie blob here"
        className="w-full h-28 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 text-xs font-mono"
      />
      {err && (
        <div className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}
      {msg && (
        <div className="text-sm text-emerald-700 dark:text-emerald-300">{msg}</div>
      )}
      <button
        disabled={busy || cookie.trim().length < 8}
        onClick={() => void capture()}
        className="rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-4 py-2 flex items-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Capture session
      </button>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        Read-only. Memso never moves money or changes anything in
        Simplifi. The cookie is stored per-user and encrypted when a
        master secret is configured.
      </p>
    </div>
  );
}
